import { useQuery } from '@tanstack/vue-query'
import { fetchVipSalaryPanel } from '~/api/promo'
import { mapVipSummary } from '~/utils/applyPlayerMe'
import type {
  VipSalaryCardDto,
  VipSalaryCardState,
  VipSalaryCardType,
  VipSalaryLevelDto,
  VipSalaryPanelDto,
  VipSalaryRulesDto,
} from '~/types/api/promo'
import type { VipLevelBenefit, VipRewardCard, VipRewardKind, VipStatus } from '../types'

export interface VipSalaryViewModel {
  status: VipStatus
  /** 0–100，直接取自后端 vip.progress_pct */
  progressPercent: number
  rewardCards: VipRewardCard[]
  levelBenefits: VipLevelBenefit[]
  totalLevels: number
  rules: VipSalaryRulesDto
  /** 可领取卡片数（红点） */
  badge: number
}

const CARD_META: Record<VipSalaryCardType, { kind: VipRewardKind; icon: string }> = {
  UPGRADE: { kind: 'upgrade', icon: '🎉' },
  DAILY: { kind: 'daily', icon: '📅' },
  WEEKLY: { kind: 'weekly', icon: '🗓️' },
  MONTHLY: { kind: 'monthly', icon: '📆' },
}

/** 按钮文案；LOCKED 且有 next_at 时由卡片按设备时区显示开放时刻 / 倒计时（VipRewardCard） */
const actionTextFor = (
  state: VipSalaryCardState,
  t: (key: string, values?: Record<string, unknown>) => string,
): string => {
  switch (state) {
    case 'AVAILABLE':
      return t('promo.vip.action.AVAILABLE')
    case 'PROCESSING':
      return t('promo.vip.action.PROCESSING')
    case 'CLAIMED':
      return t('promo.vip.action.CLAIMED')
    case 'EXPIRED':
      return t('promo.vip.action.EXPIRED')
    case 'LOCKED':
      return t('promo.vip.action.LOCKED_NO_DATE')
    case 'DISABLED':
    default:
      return t('promo.vip.action.DISABLED')
  }
}

const mapCard = (
  card: VipSalaryCardDto,
  t: (key: string, values?: Record<string, unknown>) => string,
): VipRewardCard => {
  const meta = CARD_META[card.type]
  return {
    kind: meta.kind,
    icon: meta.icon,
    label: t(`promo.vip.cardType.${card.type}`),
    amount: Number(card.amount) || 0,
    actionText: actionTextFor(card.state, t),
    claimable: card.state === 'AVAILABLE',
    claimed: card.state === 'CLAIMED',
    state: card.state,
    nextAt: card.next_at,
    expireAt: card.expire_at,
    cycleNo: card.cycle_no,
  }
}

const mapLevel = (level: VipSalaryLevelDto): VipLevelBenefit => ({
  level: level.level,
  name: level.name,
  threshold: Number(level.threshold) || 0,
  upgrade_amount: Number(level.upgrade_amount) || 0,
  daily_amount: Number(level.daily_amount) || 0,
  weekly_amount: Number(level.weekly_amount) || 0,
  monthly_amount: Number(level.monthly_amount) || 0,
})

export function mapSalaryPanel(
  data: VipSalaryPanelDto,
  t: (key: string, values?: Record<string, unknown>) => string,
  vip = mapVipSummary(data.vip),
): VipSalaryViewModel {
  return {
    status: {
      currentLevel: vip.level,
      nextLevel: vip.nextLevel,
      amountToNext: Number(vip.gapAmount) || 0,
    },
    progressPercent: Math.round(vip.progressPct),
    rewardCards: (data.cards ?? []).map(card => mapCard(card, t)),
    levelBenefits: (data.levels ?? []).map(mapLevel),
    totalLevels: data.levels?.length ?? 0,
    rules: data.rules,
    badge: data.badge,
  }
}

async function fetchVipSalaryViewModel(
  t: (key: string, values?: Record<string, unknown>) => string,
): Promise<VipSalaryViewModel> {
  const data = await fetchVipSalaryPanel()
  const vip = mapVipSummary(data.vip)
  const userStore = useUserStore()
  userStore.setVip(vip)
  userStore.setRewardBadges({ salary: data.badge })
  return mapSalaryPanel(data, t, vip)
}

/** VIP 俸禄面板：等级/进度/日周月俸禄卡片/等级权益表/保级规则，一次请求供 vip 页各区块复用 */
export function useVipSalaryPanel() {
  const { t } = useI18n()
  return useQuery({
    queryKey: ['promo', 'vip-salary'],
    queryFn: () => fetchVipSalaryViewModel(t),
    staleTime: 30_000,
  })
}
